import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Check, X, Loader2, Calendar, Phone, Mail, User, Trash2 } from 'lucide-react';

const Customers = () => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        fetchCustomers();
    }, []);

    const fetchCustomers = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/viewcustomers`, {
                headers: { authorization: `Bearer ${token}` }
            });
            setCustomers(res.data.customers);
        } catch (err) {
            console.error("Fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this customer?")) return;

        setDeletingId(id);
        try {
            const token = localStorage.getItem('adminToken');
            await axios.delete(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/deletecustomer/${id}`, {
                headers: { authorization: `Bearer ${token}` }
            });
            setCustomers(customers.filter(customer => customer._id !== id));
        } catch (err) {
            console.error("Delete error:", err);
            alert("Failed to delete Customer");
        } finally {
            setDeletingId(null);
        }
    };

    if (loading) return (
        <div className="p-10 flex flex-col items-center justify-center min-h-[400px]">
            <Loader2 className="animate-spin text-orange-500 mb-2" size={32} />
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Loading Database</p>
        </div>
    );

    return (
        <div className="p-4 md:p-6 lg:p-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl md:text-3xl font-black uppercase italic leading-none">
                        Manage <span className="text-orange-500">Customers</span>
                    </h1>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-2">
                        {customers.length} Registered Profiles
                    </p>
                </div>
            </div>

            {/* --- DESKTOP TABLE VIEW --- */}
            <div className="hidden md:block bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-slate-900 text-white text-[10px] uppercase tracking-[0.2em]">
                        <tr>
                            <th className="p-5">SL</th>
                            <th className="p-5">Customer Details</th>
                            <th className="p-5">Contact Info</th>
                            <th className="p-5 text-center">Registration</th>
                            <th className="p-5 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm font-medium text-slate-700 divide-y divide-slate-50">
                        {customers.map((item, index) => (
                            <tr key={item._id} className="hover:bg-orange-50/30 transition-all group">
                                <td className="p-5 text-slate-300 font-black italic">{String(index + 1).padStart(2, '0')}</td>
                                <td className="p-5">
                                    <div className="flex flex-col">
                                        <span className="text-slate-900 font-black uppercase italic tracking-tight text-sm group-hover:text-orange-600 transition-colors">
                                            {item.customerName || "Unnamed Lead"}
                                        </span>
                                        <span className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">ID: {item._id.slice(-6)}</span>
                                    </div>
                                </td>
                                <td className="p-5">
                                    <div className="flex flex-col gap-1">
                                        <div className="flex items-center gap-2 text-slate-600 font-bold text-xs">
                                            <Phone size={12} className="text-orange-500" /> {item.customerPhone || "N/A"}
                                        </div>
                                        <div className="flex items-center gap-2 text-slate-400 text-[11px]">
                                            <Mail size={12} /> {item.customerEmail}
                                        </div>
                                    </div>
                                </td>
                                <td className="p-5 text-center">
                                    <div className="inline-flex items-center gap-2 bg-slate-100 px-3 py-1 rounded-full text-slate-500 text-[10px] font-bold uppercase">
                                        <Calendar size={10} />
                                        {new Date(item.createdAt).toLocaleDateString('en-GB')}
                                    </div>
                                </td>
                                <td className="p-5">
                                    <div className="flex items-center justify-center gap-2">
                                        <button className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center hover:bg-emerald-600 hover:text-white transition-all">
                                            <Check size={18} strokeWidth={3} />
                                        </button>
                                        <button 
                                            disabled={deletingId === item._id}
                                            onClick={() => handleDelete(item._id)}
                                            className="w-9 h-9 rounded-xl bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-600 hover:text-white transition-all disabled:opacity-50"
                                        >
                                            {deletingId === item._id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={18} />}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* --- MOBILE CARD VIEW --- */}
            <div className="md:hidden space-y-4">
                {customers.map((item, index) => (
                    <div key={item._id} className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-1 h-full bg-orange-500"></div>
                        
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <p className="text-[10px] font-black text-slate-300 uppercase italic mb-1">Customer #{index + 1}</p>
                                <h3 className="text-sm font-black text-slate-900 uppercase italic tracking-tight">{item.customerName}</h3>
                            </div>
                            <div className="bg-slate-900 text-white text-[9px] font-bold px-2 py-1 rounded">
                                {new Date(item.createdAt).toLocaleDateString('en-GB')}
                            </div>
                        </div>

                        <div className="space-y-2 mb-5">
                            <div className="flex items-center gap-3 text-slate-600">
                                <Phone size={14} className="text-orange-500" />
                                <span className="text-xs font-bold">{item.customerPhone || "No Phone"}</span>
                            </div>
                            <div className="flex items-center gap-3 text-slate-600">
                                <Mail size={14} className="text-orange-500" />
                                <span className="text-xs font-bold truncate">{item.customerEmail}</span>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <button className="flex items-center justify-center gap-2 bg-emerald-50 text-emerald-600 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest">
                                <Check size={14} strokeWidth={3} /> Verify
                            </button>
                            <button 
                                onClick={() => handleDelete(item._id)}
                                disabled={deletingId === item._id}
                                className="flex items-center justify-center gap-2 bg-red-50 text-red-600 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest"
                            >
                                {deletingId === item._id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {customers.length === 0 && (
                <div className="text-center py-20 bg-white rounded-3xl border-2 border-dashed border-slate-100">
                    <User className="mx-auto text-slate-200 mb-4" size={48} />
                    <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">No customers found in database</p>
                </div>
            )}
        </div>
    );
};

export default Customers;